import React from "react";
import { AlertOctagon, RotateCw, Home } from "lucide-react";

/**
 * V2.0.71 — Global React ErrorBoundary.
 *
 * Purpose: production users reported that certain chat prompts caused the
 * entire screen to turn black (uncaught render throw in FloorPlan2D /
 * ThreeViewport after receiving a structurally-invalid blueprint from the
 * AI). Without this boundary, one bad blueprint object crashed the whole
 * SPA and forced a hard refresh.
 *
 * Behaviour: catches any thrown error during render / lifecycle in the
 * wrapped subtree, shows an actionable recovery panel, and reports the
 * error to the backend so we can see the stack trace server-side. The
 * user can either reload (their in-flight design is auto-saved after
 * each successful chat turn) or return to the dashboard.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error?.message || "An unexpected error occurred.",
      stack: error?.stack || "",
    };
  }

  componentDidCatch(error, info) {
    // Best-effort backend log so we can see production crashes we
    // otherwise never learn about. Fire-and-forget; if it fails we
    // still render the recovery UI.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/client-errors`;
      fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: (error?.stack || "").slice(0, 8000),
          component_stack: (info?.componentStack || "").slice(0, 8000),
          url: typeof window !== "undefined" ? window.location.href : "",
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      }).catch(() => {
        /* network down / adblock — nothing we can do */
      });
    } catch {
      /* keep the recovery UI mounted no matter what */
    }
    // Also log locally so the user can screenshot the console for us.
    console.error("[ErrorBoundary]", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6 py-12"
      >
        <div className="max-w-lg w-full border border-rose-500/40 bg-zinc-900/60 p-8 shadow-[0_24px_64px_rgba(0,0,0,0.6)]">
          <div className="flex items-center gap-2">
            <AlertOctagon className="w-4 h-4 text-rose-400" />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-rose-300">
              // Something went wrong
            </div>
          </div>
          <h1 className="mt-4 font-display text-2xl text-zinc-50 tracking-tight leading-snug">
            The editor hit an unexpected error.
          </h1>
          <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
            Your latest saved design is safe — every successful chat turn
            auto-saves before we render. Please reload to reopen your
            workspace. If this keeps happening, try rephrasing the prompt
            (e.g. break a large restructure into two smaller steps).
          </p>
          <details className="mt-4 border border-zinc-800 bg-zinc-950 p-3">
            <summary className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer">
              // technical details
            </summary>
            <pre
              data-testid="app-error-boundary-message"
              className="mt-2 text-[11px] text-rose-300 font-mono whitespace-pre-wrap break-all"
            >
              {this.state.message}
            </pre>
          </details>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              data-testid="app-error-boundary-reload"
              onClick={() => {
                if (typeof window !== "undefined") window.location.reload();
              }}
              className="inline-flex items-center justify-center gap-2 bg-brass-500 hover:bg-brass-400 text-zinc-950 font-mono text-[11px] uppercase tracking-widest px-4 py-3 transition-colors"
            >
              <RotateCw className="w-3.5 h-3.5" />
              Reload workspace
            </button>
            <a
              href="/dashboard"
              data-testid="app-error-boundary-dashboard"
              className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-brass-500 text-zinc-200 hover:text-brass-200 font-mono text-[11px] uppercase tracking-widest px-4 py-3 transition-colors"
            >
              <Home className="w-3.5 h-3.5" />
              Back to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
